import { PagamentoResponse, PagamentoTipo, EventoExtraordinarioTipo, TipoLiquidacao } from 'common/genericTypes';
import { GenericDrawer, GenericDrawerTextContent } from 'components/genericDrawer';
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import ErrorIcon from '@material-ui/icons/Error';
import { Typography } from '@material-ui/core';
import { formatEightDecimalsCurrency } from 'utils/number';
import { Dropdown, MdSpace, SmSpace, TextField } from 'm2m.components';
import { labelsCaracteristicas } from 'pages/cra/registro/labels';
import { tipoLiquidacaoDescriptions } from 'common/genericDescriptions';

interface ValidarLiquidacaoDrawerProps {
  pagamento?: PagamentoResponse;
  opened: boolean;
  onClose(): void;
  onSubmitSuccess(reject?: boolean): void;
  reject?: boolean;
}

const LIMITE_PERCENTUAL_RESIDUAL = 1.40000000;

const ValidarLiquidacaoDrawerComponent: React.FC<ValidarLiquidacaoDrawerProps> = (props) => {
  const { pagamento, opened, onClose, onSubmitSuccess, reject } = props;
  const dispatch = useDispatch();
  const { postReprovarPagamento, postAprovarPagamento } = useSelector((state: RootState) => state.instrumentoApi);
  const [puRemanescenteApurado, setPuRemanescenteApurado] = useState<number>(0)
  const [tipoLiquidacao, setTipoLiquidacao] = useState<TipoLiquidacao>(TipoLiquidacao.B3);
  const [motivoReprovacao, setMotivoReprovacao] = useState<string>('');
  const exclusions = ['OUTRAS', 'FS'];
  const tipoLiquidacaoList = Object.keys(tipoLiquidacaoDescriptions).filter((item) => !exclusions.includes(item));

  const handleValidarLiquidacao = async () => {
    if (!pagamento) return;
    try {
      if (reject) {
        await postReprovarPagamento(dispatch, pagamento.id, motivoReprovacao || undefined, true);
      } else {
        await postAprovarPagamento(dispatch, pagamento.id, tipoLiquidacao, true);
      }
      onSubmitSuccess(reject);
      setMotivoReprovacao('');
    } catch (error) { }
  };

  const getCalcularAmortizacao = useSelector((state: RootState) => state.instrumentoApi.getCalcularAmortizacao);

  const getCalculoPercentual = useCallback(async () => {
    var request = { percentual: undefined, valorPagamento: pagamento?.valorPagamento, valorUnitario: pagamento?.instrumentoFinanceiro.valorNominalUnitarioEmissao, puRemanescente: pagamento?.instrumentoFinanceiro.puRemanescente };
    try {
      const result = await getCalcularAmortizacao(dispatch, request, false);

      if (pagamento?.instrumentoFinanceiro.valorNominalUnitarioEmissao) {
        const percentualDoPuRemanescenteApurado = (result.puRemanescenteApurado / pagamento?.instrumentoFinanceiro.valorNominalUnitarioEmissao) * 100
        setPuRemanescenteApurado(percentualDoPuRemanescenteApurado)
      }

    } catch (error) { }
  }, []);

  useEffect(() => {
    getCalculoPercentual()
  }, [pagamento])

  const pagamentoDescriptions: { [key: string]: string } = {
    [PagamentoTipo.Amortizacao]: 'Pagamento Amortização',
    [PagamentoTipo.Juros]: 'Pagamento Juros',
    [EventoExtraordinarioTipo.ResgateAntecipadoTotal]: 'Valor a ser pago',
  };

  const textContents: GenericDrawerTextContent[] = [
    {
      text: `Tem certeza que deseja ${reject ? 'reprovar' : 'aprovar'
        } a solicitação de liquidação do instrumento financeiro ${pagamento?.instrumentoFinanceiro.codigo} ?`,
    },
  ];

  if (pagamento?.valorPagamento && pagamentoDescriptions[pagamento.tipo]) {
    textContents.push({
      subtitle: pagamentoDescriptions[pagamento.tipo],
      text: formatEightDecimalsCurrency(pagamento.valorPagamento),
    });
  }

  if (pagamento?.percentual) {
    textContents.push({
      subtitle: '% Amortização:',
      text: `${pagamento.percentual} %`,
    });
  }

  if (pagamento?.valorJuros) {
    textContents.push({
      subtitle: 'Valor Juros:',
      text: `${formatEightDecimalsCurrency(pagamento.valorJuros)}`,
    });
  }

  if (pagamento?.valorMulta) {
    textContents.push({
      subtitle: 'Valor Multa:',
      text: formatEightDecimalsCurrency(pagamento.valorMulta) || '',
    });
  }

  if (pagamento?.valorMora) {
    textContents.push({
      subtitle: 'Valor Mora:',
      text: formatEightDecimalsCurrency(pagamento.valorMora) || '',
    });
  }

  if (pagamento?.valorPremio) {
    textContents.push({
      subtitle: 'Valor Prêmio:',
      text: formatEightDecimalsCurrency(pagamento.valorPremio) || '',
    });
  }

  if (
    pagamento &&
    [EventoExtraordinarioTipo.Amortizacao, EventoExtraordinarioTipo.ResgateAntecipadoTotal].includes(
      pagamento.tipo as EventoExtraordinarioTipo
    )
  ) {
    textContents.push({
      subtitle: 'Liquidação direta:',
      text: pagamento.liquidacaoDireta ? 'Sim' : 'Não',
    });
  }

  return (
    <GenericDrawer
      opened={opened}
      title={`${reject ? 'Reprovar' : 'Aprovar'} Solicitação de Liquidação`}
      textContents={textContents}
      onClose={onClose}
      buttons={[
        {
          buttonText: `${reject ? 'Reprovar' : 'Aprovar'} Liquidação`,
          buttonAction: handleValidarLiquidacao,
          buttonColor: reject ? 'error' : 'primary',
          buttonDisable: reject ? !motivoReprovacao : false,
        },
        {
          buttonText: 'Cancelar',
          buttonAction: onClose,
        },
      ]}
    >
      {reject && (
        <>
          <TextField
            label="Motivo da Reprovação"
            value={motivoReprovacao}
            onChange={(value: string) => setMotivoReprovacao(value)}
            required
            multiline
            rows={3}
          />
          <SmSpace />
        </>
      )}
      {!reject && (
        <>
          <Dropdown
            label={labelsCaracteristicas.tipoLiquidacao}
            required
            options={tipoLiquidacaoList.map((item: string) => ({
              value: item,
              description: tipoLiquidacaoDescriptions[item],
            }))}
            value={tipoLiquidacao}
            onChange={(value) => setTipoLiquidacao(value as TipoLiquidacao)}
          />
          <MdSpace />
        </>
      )}

      {pagamento?.ultimoPagamento && puRemanescenteApurado >= LIMITE_PERCENTUAL_RESIDUAL &&
        <div
          style={{
            marginBottom: '20px',
            padding: '20px',
            border: '2px solid #FFCF40',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ErrorIcon style={{ marginRight: 10 }} fontSize="small" />
          <Typography variant="body1">
            {`ATENÇÃO Evento ${pagamento?.instrumentoFinanceiro.codigo} será liquidado com valor residual, você tem certeza disso?. Ao aprova-lo, o valor residual somente poderá ser liquidado por RESGATE ANTECIPADO`}</Typography>
        </div>
      }
    </GenericDrawer>
  );
};

export const ValidarLiquidacaoDrawer = ValidarLiquidacaoDrawerComponent;
